import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AGENT_STATE_MAP, type AgentState } from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { dateTime, dateTimeSec, num, time } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, Modal, PageHead, Pager } from '../../components/ui';
import { DesktopAppNotice } from '../../components/DesktopAppNotice';
import { OwnerChip, useTeamDirectory } from '../../components/People';
import { useToast } from '../../components/Toast';

/**
 * صورةٌ محميّة تُجلب بالرمز لا برابطٍ مباشر.
 *
 * رمزُ الوصول يُرسَل في ترويسة `Authorization`، ووسمُ `<img src>` العاديّ لا
 * يحملها — فكان طلبُ اللقطة يعود 401 وتظهر البطاقةُ فارغة. فتُجلب البتاتُ
 * عبر `api` (يحمل الرمز والمستأجر) وتُعرض من `blob:`، ويُبطَل الكائنُ عند
 * التغيير فلا تتراكم الذاكرة. ولا رمزَ في الرابط — وهو ما تمنعه القاعدة.
 */
function AuthImg({ path, alt, className }: { path: string; alt: string; className?: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let url: string | null = null;
    setSrc(null);
    setFailed(false);

    api<Blob>(path, { raw: true })
      .then((blob) => {
        if (!alive) return;
        url = URL.createObjectURL(blob);
        setSrc(url);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });

    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [path]);

  if (failed) {
    return (
      <div className="cap-broken tiny mute">
        <Icon name="alert" size={18} />
        تعذّر عرض اللقطة
      </div>
    );
  }
  if (!src) return <div className="cap-loading skeleton" />;
  // eslint-disable-next-line jsx-a11y/img-redundant-alt
  return <img src={src} alt={alt} className={className} loading="lazy" />;
}

/**
 * مركز مراقبة الموظف.
 *
 * لوحتان: «الآن» تُري كل موظفٍ متّصلًا وعلى أيّ شاشةٍ يعمل هذه اللحظة، ومن
 * أذِن بمشاركة شاشته تُشاهَد صورتُها الحيّة. و«السجل» يعرض ما التُقط —
 * لقطاتٍ ومقاطع — مخزَّنًا في النظام ويُفتح فيه.
 *
 * ## الصورة الحيّة — وحدّها بصراحة
 *
 * «على أيّ شاشة» يُعرف من نبضة نشاطٍ يبعثها كل متصفّح بلا إذن. أمّا الصورة
 * الحيّة فتحتاج مشاركة الشاشة: لا يُشاهَد إلا من فتح جلسته وأذِن بها، ومؤشّر
 * المشاركة يبقى أمامه طوال النظر. هذا حدٌّ يفرضه المتصفح لا نقصٌ في النظام،
 * وإعلامُ الموظفين بسياسة المراقبة مسؤوليتكم.
 */

interface BoardAgent {
  userId: string;
  state: AgentState;
  recordingOn: boolean;
  monitorOn: boolean;
  watchedNow: boolean;
  live: boolean;
  lastFrameAt: string | null;
  online: boolean;
  currentPath: string | null;
  currentLabel: string | null;
  lastSeenAt: string | null;
}

export function MonitorScreen() {
  const [tab, setTab] = useState<'now' | 'log'>('now');

  return (
    <div className="page">
      <PageHead title="مركز المراقبة" subtitle="من يعمل الآن وعلى أيّ شاشة، وسجلّ ما التُقط" />

      <DesktopAppNotice context="monitor" />

      <div className="tabs" role="tablist">
        <button
          className="tab"
          role="tab"
          aria-selected={tab === 'now'}
          onClick={() => setTab('now')}
        >
          <Icon name="video" size={15} /> الآن
        </button>
        <button
          className="tab"
          role="tab"
          aria-selected={tab === 'log'}
          onClick={() => setTab('log')}
        >
          <Icon name="clock" size={15} /> السجل
        </button>
      </div>

      {tab === 'now' ? <LiveBoard /> : <CapturesLog />}
    </div>
  );
}

/* ==========================================================================
   لوحة «الآن»
   ========================================================================== */

function LiveBoard() {
  const [watching, setWatching] = useState<string | null>(null);

  const board = useQuery({
    queryKey: ['cc', 'monitor-board'],
    queryFn: () => api<{ items: BoardAgent[] }>('/callcenter/monitor/board'),
    refetchInterval: 5000,
  });

  const agents = board.data?.items ?? [];
  /* المتّصل أولًا، ثم من له صورةٌ حيّة، ثم الباقي — الأنشط في الأعلى. */
  const sorted = [...agents].sort(
    (a, b) => Number(b.online) - Number(a.online) || Number(b.live) - Number(a.live),
  );
  const online = agents.filter((a) => a.online).length;

  return (
    <>
      <div className="alert alert-info mb-4">
        «على أيّ شاشة» يُعرف بلا إذن. والصورة الحيّة تحتاج أن يبدأ الموظف مشاركة شاشته، ويرى مؤشّرها
        طوال مشاهدتك — لا التقاط خفيّ. وإعلام الموظفين بسياسة المراقبة مسؤوليتكم.
      </div>

      {board.isSuccess && agents.length === 0 && (
        <EmptyState
          title="لا أحد متّصل الآن"
          text="يظهر هنا كلُّ موظفٍ يفتح النظام، والشاشة التي يعمل عليها لحظةً بلحظة."
        />
      )}

      {agents.length > 0 && (
        <p className="tiny mute mb-3">
          {num(online)} متّصل من {num(agents.length)} — يتحدّث كل خمس ثوانٍ.
        </p>
      )}

      <div className="mon-grid">
        {sorted.map((a) => (
          <MonitorTile
            key={a.userId}
            agent={a}
            active={watching === a.userId}
            onWatch={() => setWatching(a.userId)}
            onStop={() => setWatching((w) => (w === a.userId ? null : w))}
          />
        ))}
      </div>
    </>
  );
}

/* ==========================================================================
   بطاقة موظف — نشاطُه الآن، وشاشتُه حين تُشاهَد
   ========================================================================== */

function MonitorTile({
  agent,
  active,
  onWatch,
  onStop,
}: {
  agent: BoardAgent;
  active: boolean;
  onWatch: () => void;
  onStop: () => void;
}) {
  const { byId } = useTeamDirectory();
  const name = byId.get(agent.userId)?.name ?? 'مستخدم';
  const stateDef = AGENT_STATE_MAP[agent.state];
  const imgRef = useRef<HTMLImageElement>(null);
  const [stale, setStale] = useState(false);
  /* يشارك شاشته الآن: متّصلٌ والمراقبةُ مفعّلة عنده — فيمكن طلبُ مشاهدته. */
  const canWatch = agent.online && agent.monitorOn;

  /*
   * الإطار يُجلب كل ثانيتين ما دامت البطاقة نشطة.
   *
   * كائن blob لكل إطار يُبطَل بعد الذي يليه: تركها تتراكم يسرّب ذاكرة
   * المتصفح على وردية كاملة. والجلب يجدّد طلب المشاهدة في الخادم ضمنًا.
   */
  useEffect(() => {
    if (!active) return;
    let url: string | null = null;
    let alive = true;

    const tick = async () => {
      try {
        /* عبر `api` لا `fetch` مباشر: رمزُ الوصول في الترويسة، وبدونه 401. */
        const blob = await api<Blob | null>(`/callcenter/monitor/frame/${agent.userId}`, {
          raw: true,
        });
        if (!alive) return;
        if (!blob) {
          setStale(true);
          return;
        }
        const next = URL.createObjectURL(blob);
        if (imgRef.current) imgRef.current.src = next;
        if (url) URL.revokeObjectURL(url);
        url = next;
        setStale(false);
      } catch {
        setStale(true);
      }
    };

    void tick();
    const timer = window.setInterval(tick, 2000);

    return () => {
      alive = false;
      window.clearInterval(timer);
      if (url) URL.revokeObjectURL(url);
      void api('/callcenter/monitor/unwatch', {
        method: 'POST',
        body: { userId: agent.userId },
      }).catch(() => undefined);
    };
  }, [active, agent.userId]);

  return (
    <div className={`mon-tile${active ? ' on' : ''}`}>
      <div className="mon-tile-head">
        <OwnerChip ownerId={agent.userId} size={24} />
        {/* الحالة تُعرض لمن هو في مركز الاتصال؛ سواه «متّصل» يكفيه. */}
        {agent.state !== 'OFFLINE' ? (
          <Badge kind={stateDef?.kind ?? 'neutral'}>{stateDef?.label ?? agent.state}</Badge>
        ) : (
          <Badge kind={agent.online ? 'success' : 'neutral'}>
            {agent.online ? 'متّصل' : 'غير متّصل'}
          </Badge>
        )}
        {agent.recordingOn && (
          <span className="cc-flag rec">
            <span className="cc-rec-dot" /> تسجيل
          </span>
        )}
        <span style={{ marginInlineStart: 'auto' }}>
          {active ? (
            <button className="btn btn-sm btn-danger" onClick={onStop}>
              إيقاف المشاهدة
            </button>
          ) : (
            <button
              /*
               * يُتاح متى كان الموظّفُ يشارك شاشته (متّصلٌ والمراقبةُ مفعّلة)
               * لا حين وُجد إطارٌ حيّ: الإطارُ لا يُدفع إلّا بعد بدء المشاهدة،
               * فاشتراطُه يقفل البابَ على نفسه — لا يُشاهَد حتى يُشاهَد.
               */
              className="btn btn-sm btn-primary"
              disabled={!canWatch}
              onClick={onWatch}
              title={canWatch ? 'شاهد شاشته الآن' : 'لم يبدأ مشاركة شاشته'}
            >
              <Icon name="video" size={14} />
              مشاهدة
            </button>
          )}
        </span>
      </div>

      {/* الشاشة الحالية: يُعرف بلا مشاركةٍ، فيظهر لكل متّصل. */}
      <div className="mon-activity">
        <Icon name="chartBar" size={13} />
        <span className="strong">{agent.currentLabel ?? '—'}</span>
        {agent.lastSeenAt && (
          <span className="tiny mute" style={{ marginInlineStart: 'auto' }}>
            منذ {time(agent.lastSeenAt)}
          </span>
        )}
      </div>

      <div className="mon-screen">
        {active ? (
          <>
            {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
            <img ref={imgRef} alt={`شاشة ${name}`} className="mon-frame" />
            {stale && <div className="mon-stale">بانتظار الإطار…</div>}
            <span className="mon-live-dot" title="مشاهدة حيّة">
              ● حيّ
            </span>
          </>
        ) : (
          <div className="mon-idle">
            <Icon name="video" size={28} />
            <span className="tiny mute">
              {canWatch ? 'اضغط «مشاهدة» لعرض الشاشة' : 'لم يبدأ مشاركة شاشته'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   لوحة «السجل» — ما التُقط
   ========================================================================== */

interface Capture {
  id: string;
  userId: string;
  kind: 'SHOT' | 'SEGMENT';
  mime: string;
  bytes: number;
  storage: 'DB' | 'DRIVE';
  path: string | null;
  label: string | null;
  capturedAt: string;
  expiresAt: string;
  inline: boolean;
}

/** كم يحذف زرُّ التحديد دفعةً — يطابق حدَّ المخطَّط في الخادم. */
const MAX_IDS_PER_DELETE = 500;

function CapturesLog() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<'' | 'SHOT' | 'SEGMENT'>('');
  const [opened, setOpened] = useState<Capture | null>(null);
  /* المحدَّدون بمعرّفاتهم — والاختيارُ يُمسح عند تغيّر المرشِّح أو الصفحة. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /* حوارُ تأكيدٍ قبل الحذف: 'ids' للمحدَّد، 'all' لكلّ النتائج. */
  const [confirm, setConfirm] = useState<null | 'ids' | 'all'>(null);

  const filter = { page, kind: kind || undefined };

  const list = useQuery({
    queryKey: ['cc', 'captures', page, kind],
    queryFn: () =>
      api<{
        items: Capture[];
        total: number;
        page: number;
        pageSize: number;
        pageCount: number;
      }>(`/callcenter/monitor/captures${qs(filter)}`),
  });

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  const setKindReset = (k: '' | 'SHOT' | 'SEGMENT') => {
    setKind(k);
    setPage(1);
    setSelected(new Set());
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allShownSelected = items.length > 0 && items.every((c) => selected.has(c.id));
  const toggleAllShown = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allShownSelected) items.forEach((c) => next.delete(c.id));
      else items.forEach((c) => next.add(c.id));
      return next;
    });

  const del = useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) =>
      api<{ deleted: number }>('/callcenter/monitor/captures', {
        method: 'DELETE',
        body: body.all ? { all: true, kind: kind || undefined } : { ids: body.ids },
      }),
    onSuccess: (r) => {
      setConfirm(null);
      setSelected(new Set());
      setPage(1);
      toast(`حُذف ${num(r.deleted)} من التسجيلات.`, 'success');
      void queryClient.invalidateQueries({ queryKey: ['cc', 'captures'] });
    },
    onError: (e) => {
      setConfirm(null);
      toast(e instanceof Error ? e.message : 'تعذّر الحذف.');
    },
  });

  return (
    <>
      <div className="toolbar sticky-bar">
        <div className="row" style={{ gap: 2 }}>
          <button
            className={`btn btn-sm${!kind ? ' btn-primary' : ''}`}
            onClick={() => setKindReset('')}
          >
            الكل
          </button>
          <button
            className={`btn btn-sm${kind === 'SHOT' ? ' btn-primary' : ''}`}
            onClick={() => setKindReset('SHOT')}
          >
            لقطات
          </button>
          <button
            className={`btn btn-sm${kind === 'SEGMENT' ? ' btn-primary' : ''}`}
            onClick={() => setKindReset('SEGMENT')}
          >
            مقاطع
          </button>
        </div>

        {items.length > 0 && (
          <div className="row" style={{ gap: 'var(--sp-2)', marginInlineStart: 'auto' }}>
            <button className="btn btn-sm" onClick={toggleAllShown}>
              {allShownSelected ? 'إلغاء تحديد الظاهر' : 'تحديد الظاهر'}
            </button>
            <button
              className="btn btn-sm btn-danger"
              disabled={selected.size === 0 || del.isPending}
              onClick={() => setConfirm('ids')}
            >
              <Icon name="trash" size={14} />
              حذف المحدَّد{selected.size ? ` (${num(selected.size)})` : ''}
            </button>
            <button
              className="btn btn-sm btn-ghost link-danger"
              disabled={del.isPending}
              onClick={() => setConfirm('all')}
              title="يحذف كل التسجيلات في هذا التصنيف عبر كل الصفحات"
            >
              حذف الكل ({num(total)})
            </button>
          </div>
        )}
      </div>

      {list.isSuccess && items.length === 0 && (
        <EmptyState
          title="لا تسجيلات بعد"
          text="حين تُفعَّل المراقبة (لقطات أو فيديو) يظهر الملتقَط هنا، ويُحذف تلقائيًّا بعد مدة الاحتفاظ."
        />
      )}

      <div className="cap-grid">
        {items.map((c) => {
          const isSel = selected.has(c.id);
          return (
            <div key={c.id} className={`cap-card${isSel ? ' is-selected' : ''}`}>
              {/* مربّعُ التحديد فوق البطاقة — نقرتُه تُحدِّد لا تفتح العارض. */}
              <label className="cap-check" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={isSel} onChange={() => toggle(c.id)} />
              </label>
              <button className="cap-open" onClick={() => setOpened(c)}>
                <div className="cap-thumb">
                  {c.kind === 'SHOT' ? (
                    <AuthImg path={`/callcenter/monitor/captures/${c.id}/content`} alt="" />
                  ) : (
                    <div className="cap-video-badge">
                      <Icon name="video" size={22} />
                      <span className="tiny">مقطع</span>
                    </div>
                  )}
                </div>
                <div className="cap-meta">
                  <OwnerChip ownerId={c.userId} size={20} />
                  <span className="tiny mute">{c.label ?? '—'}</span>
                  <span className="tiny mute">{dateTimeSec(c.capturedAt)}</span>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {list.data && total > 0 && (
        <Pager
          page={list.data.page}
          pageCount={list.data.pageCount}
          total={total}
          pageSize={list.data.pageSize}
          onPage={(p) => {
            setPage(p);
            setSelected(new Set());
          }}
        />
      )}

      {confirm && (
        <Modal
          title={confirm === 'all' ? 'حذف كل التسجيلات' : 'حذف المحدَّد'}
          onClose={() => setConfirm(null)}
          footer={
            <div className="row" style={{ gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
              <button className="btn" onClick={() => setConfirm(null)}>
                إلغاء
              </button>
              <button
                className="btn btn-danger"
                disabled={del.isPending}
                onClick={() =>
                  confirm === 'all'
                    ? del.mutate({ all: true })
                    : del.mutate({ ids: [...selected].slice(0, MAX_IDS_PER_DELETE) })
                }
              >
                {del.isPending ? 'جارٍ الحذف' : 'حذف نهائيّ'}
              </button>
            </div>
          }
        >
          {confirm === 'all' ? (
            <p>
              سيُحذف <span className="strong">{num(total)}</span> تسجيلًا
              {kind ? ' من هذا التصنيف' : ''} عبر كل الصفحات. لا يمكن التراجع.
            </p>
          ) : (
            <p>
              سيُحذف{' '}
              <span className="strong">{num(Math.min(selected.size, MAX_IDS_PER_DELETE))}</span>{' '}
              تسجيلًا محدَّدًا. لا يمكن التراجع.
              {selected.size > MAX_IDS_PER_DELETE && (
                <span className="tiny mute" style={{ display: 'block' }}>
                  المحدَّد أكثر من {num(MAX_IDS_PER_DELETE)}؛ يُحذف هذا العدد الآن، فكرِّر الحذف
                  للباقي، أو استعمل «حذف الكل».
                </span>
              )}
            </p>
          )}
        </Modal>
      )}

      {opened && <CaptureViewer capture={opened} onClose={() => setOpened(null)} />}
    </>
  );
}

function CaptureViewer({ capture, onClose }: { capture: Capture; onClose: () => void }) {
  const { byId } = useTeamDirectory();
  const name = byId.get(capture.userId)?.name ?? 'مستخدم';
  const path = `/callcenter/monitor/captures/${capture.id}/content`;
  /* المقطعُ فيديو، ووسمُ `<video src>` لا يحمل الرمز كذلك — فيُجلب بلوبًا. */
  const [videoSrc, setVideoSrc] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (capture.kind !== 'SEGMENT') return undefined;
    let alive = true;
    let url: string | null = null;
    api<Blob>(path, { raw: true })
      .then((blob) => {
        if (!alive) return;
        url = URL.createObjectURL(blob);
        setVideoSrc(url);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [path, capture.kind]);

  return (
    <div className="doc-preview" role="dialog" aria-label="عرض اللقطة">
      <div className="doc-preview-bar">
        <button className="btn btn-sm btn-icon btn-ghost" onClick={onClose} aria-label="إغلاق">
          <Icon name="x" size={17} />
        </button>
        <strong className="doc-preview-title">
          {name} — {capture.label ?? '—'} · {dateTimeSec(capture.capturedAt)}
        </strong>
        <span className="tiny mute" style={{ marginInlineStart: 'auto' }}>
          يُحذف في {dateTime(capture.expiresAt)}
        </span>
      </div>
      <div className="doc-preview-body cap-viewer">
        {capture.kind === 'SHOT' ? (
          <AuthImg path={path} alt={`لقطة ${name}`} />
        ) : videoSrc ? (
          <video src={videoSrc} controls autoPlay style={{ maxWidth: '100%', maxHeight: '100%' }} />
        ) : (
          <div className="cap-loading skeleton" style={{ width: 320, height: 180 }} />
        )}
      </div>
    </div>
  );
}
